import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { getUniqueKey } from "../../../../../helpers/MathHelpers";
import { translationService } from "../../../../../services/translation/TranslationService";
import {
  MessageContent,
  ModalElement,
} from "../../modal-content-strategy-builder/ModalContentContext";
import "./DisplayModalContent.scss";

export default observer(function DisplayModalContent(props: { content: MessageContent }) {
  const getFormattedText = useCallback((modalElement: ModalElement) => {
    if (modalElement?.isEmptyLine) {
      return <br key={getUniqueKey()} />;
    }
    if (modalElement?.shouldDisplayBolded) {
      return (
        <>
          {modalElement?.beforeText}
          <b className="bolded-text" key={getUniqueKey()}>
            {modalElement?.displayText}
          </b>
          {modalElement?.afterText}
        </>
      );
    }

    if (modalElement.displayText)
      return <span key={getUniqueKey()}>{modalElement?.displayText}</span>;

    return "";
  }, []);

  const getListItems = useCallback(
    (list: ModalElement[]) => {
      const results: JSX.Element[] = [];

      let index = 0;
      while (index < list.length) {
        const item = list[index];
        if (item?.isStartItem) {
          const tempList = list.slice(index);
          const endIndex = tempList.findIndex((u) => u?.isEndItem);
          const updates = tempList.slice(0, endIndex + 1);
          results.push(<li key={getUniqueKey()}>{updates.map((u) => getFormattedText(u))}</li>);
          index += endIndex + 1;
        } else {
          const result = getFormattedText(item);
          results.push(result ? result : <></>);
          index++;
        }
      }
      return results;
    },
    [getFormattedText]
  );

  const getSection = useCallback(
    (updates: ModalElement[]) => {
      const updateItems: JSX.Element[] = [];
      let index = 0;
      while (index < updates.length) {
        const update = updates[index];
        if (update?.isStartList) {
          const tempList = updates.slice(index);
          const endListIndex = tempList.findIndex((x) => x.isEndList);

          const innerlist = tempList.slice(1, endListIndex + 1);

          index += endListIndex + 1;

          updateItems.push(
            <>
              {update?.displayText}
              <ul key={getUniqueKey()}>{getListItems(innerlist)}</ul>
            </>
          );
        } else {
          const formattedText = getFormattedText(update);
          if (formattedText) updateItems.push(formattedText);
          index++;
        }
      }

      return updateItems;
    },
    [getFormattedText, getListItems]
  );

  return (
    <>
      {props.content.updates.length > 0 && (
        <div className="modal-section section-updates">
          <div className="info-banner">{translationService.translate("general.updates")}</div>
          {getSection(props.content.updates)}
        </div>
      )}
      {props.content.invoice.length > 0 && (
        <div className="modal-section section-invoice">
          <div className="info-banner">{translationService.translate("general.invoice")}</div>
          {getSection(props.content.invoice)}
        </div>
      )}
      {props.content.info.length > 0 && (
        <div className="modal-section section-info">
          <div className="info-banner">{translationService.translate("general.info")}</div>
          {getSection(props.content.info)}
        </div>
      )}
    </>
  );
});
